<template>
  <div class="line-friend-picker">
    <p v-if="resolving" class="line-friend-empty">載入 LINE 好友...</p>
    <div v-else-if="selected" class="selected-line-friend">
      <img v-if="selected.linePictureUrl" :src="selected.linePictureUrl" alt="" />
      <div>
        <strong>{{ displayName(selected) }}</strong>
        <small>{{ selected.lineUserId }}</small>
        <small v-if="selected.lineConfigName">{{ selected.lineConfigName }}</small>
      </div>
      <button type="button" class="btn btn-sm btn-secondary" :disabled="disabled" @click="clear">
        解除關聯
      </button>
    </div>
    <template v-else>
      <div class="line-search-row">
        <input
          v-model.trim="keyword"
          class="form-control"
          :disabled="disabled"
          placeholder="輸入 LINE 顯示名稱、會員姓名或 User ID"
          @keyup.enter.prevent="search"
        />
        <button type="button" class="btn btn-secondary" :disabled="disabled || searching" @click="search">
          {{ searching ? '搜尋中...' : '搜尋' }}
        </button>
      </div>
      <p v-if="searchError" class="line-friend-error">{{ searchError }}</p>
      <div v-if="results.length" class="line-results">
        <button
          v-for="friend in results"
          :key="friend.id"
          type="button"
          class="line-result"
          :disabled="disabled"
          @click="select(friend)"
        >
          <img v-if="friend.linePictureUrl" :src="friend.linePictureUrl" alt="" />
          <span>
            <strong>{{ displayName(friend) }}</strong>
            <small>{{ friend.lineUserId }}</small>
            <small v-if="friend.lineConfigName">{{ friend.lineConfigName }}</small>
          </span>
        </button>
      </div>
      <p v-else-if="searched && !searching" class="line-friend-empty">找不到可接收推播的 LINE 好友</p>
    </template>
    <small class="form-text">{{ hint }}</small>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import lineBindingApi from '@/api/modules/lineBinding.js';

export default {
  name: 'LineFriendPicker',
  props: {
    modelValue: { type: String, default: '' },
    friend: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
    hint: { type: String, default: '僅顯示目前可接收推播的 LINE 好友。' }
  },
  emits: ['update:modelValue', 'select'],
  setup(props, { emit }) {
    const keyword = ref('');
    const searching = ref(false);
    const searched = ref(false);
    const searchError = ref('');
    const results = ref([]);
    const selected = ref(null);
    const resolving = ref(false);
    let resolveToken = 0;

    const displayName = (friend) => friend?.lineDisplayName || friend?.member?.name || '未命名好友';

    const select = (friend) => {
      selected.value = friend;
      results.value = [];
      keyword.value = '';
      searched.value = false;
      searchError.value = '';
      emit('update:modelValue', friend.lineUserId || '');
      emit('select', friend);
    };

    const clear = () => {
      selected.value = null;
      emit('update:modelValue', '');
      emit('select', null);
    };

    const search = async () => {
      if (searching.value || keyword.value.length < 2) return;
      searching.value = true;
      searched.value = false;
      searchError.value = '';
      try {
        const response = await lineBindingApi.list({
          search: keyword.value,
          page: 1,
          pageSize: 20,
          isFollowing: true,
          isPushBlocked: false,
          includeWithoutConfig: true
        });
        results.value = response?.data?.items || [];
        searched.value = true;
      } catch (error) {
        results.value = [];
        searchError.value = error.response?.data?.message || error.message || 'LINE 好友搜尋失敗';
      } finally {
        searching.value = false;
      }
    };

    const resolveSelected = async (lineUserId) => {
      const token = ++resolveToken;
      if (!lineUserId) {
        selected.value = null;
        resolving.value = false;
        return;
      }
      if (selected.value?.lineUserId === lineUserId) return;
      if (props.friend?.lineUserId === lineUserId) {
        selected.value = props.friend;
        resolving.value = false;
        return;
      }
      resolving.value = true;
      try {
        const response = await lineBindingApi.list({
          lineUserId,
          page: 1,
          pageSize: 5
        });
        if (token !== resolveToken) return;
        const items = response?.data?.items || [];
        const match = items.find(item => item.isFollowing && !item.isPushBlocked) || items[0] || null;
        selected.value = match || { lineUserId, lineDisplayName: '', linePictureUrl: '' };
      } catch (error) {
        if (token !== resolveToken) return;
        selected.value = { lineUserId, lineDisplayName: '', linePictureUrl: '' };
      } finally {
        if (token === resolveToken) resolving.value = false;
      }
    };

    watch(() => props.modelValue, (value) => {
      resolveSelected(value || '');
    }, { immediate: true });

    watch(() => props.friend, (friend) => {
      if (friend?.lineUserId && friend.lineUserId === (props.modelValue || '')) {
        selected.value = friend;
        resolving.value = false;
      }
    });

    return {
      keyword,
      searching,
      searched,
      searchError,
      results,
      resolving,
      selected,
      displayName,
      select,
      clear,
      search
    };
  }
};
</script>

<style scoped>
.line-search-row { display: flex; gap: 10px; }
.line-results {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: grid;
  margin-top: 8px;
  max-height: 260px;
  overflow-y: auto;
}
.line-result {
  align-items: center;
  background: #fff;
  border: 0;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  text-align: left;
}
.line-result:last-child { border-bottom: 0; }
.line-result:hover { background: #f8fafc; }
.line-result img, .selected-line-friend img {
  border-radius: 50%;
  height: 42px;
  object-fit: cover;
  width: 42px;
}
.line-result span, .selected-line-friend div { display: grid; gap: 3px; min-width: 0; }
.line-result small, .selected-line-friend small {
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
}
.selected-line-friend {
  align-items: center;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  padding: 12px;
}
.selected-line-friend div { flex: 1; }
.form-text { color: #64748b; display: block; margin-top: 6px; }
.line-friend-empty, .line-friend-error { color: #64748b; font-size: 13px; margin: 8px 0 0; }
.line-friend-error { color: #b91c1c; }
@media (max-width: 768px) {
  .line-search-row { align-items: stretch; flex-direction: column; }
}
</style>
